import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';
import { useCredentialsByType } from '../../../../hooks/useCredentials';
import { useCustomerGroups } from '../../../../hooks/useCustomerGroups';
import { useEmailTemplates } from '../../../../hooks/useEmailTemplates';

const SMTPEmailConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['recipient', 'smtp', 'email']));
  const [useCredential, setUseCredential] = useState(!!node.data.config?.credentialId);
  const [useTemplate, setUseTemplate] = useState(!!node.data.config?.emailTemplateId);
  const { credentials, loading: credentialsLoading } = useCredentialsByType('smtp');
  const { groups, loading: groupsLoading } = useCustomerGroups();
  const { templates, loading: templatesLoading } = useEmailTemplates();
  
  // Recipient mode: 'single' = egy címzett, 'group' = ügyfélcsoport
  const recipientMode = node.data.config?.recipientMode || 'single';
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // Credential használat váltása
  const handleCredentialToggle = (checked: boolean) => {
    setUseCredential(checked);
    if (!checked) {
      updateConfig('credentialId', undefined);
    }
  };

  return (
    <div className="space-y-3">
      {/* Címzett Típusa */}
      <CollapsibleSection
        id="recipient"
        title="Címzett Kiválasztása"
        icon="👥"
        color="blue"
        isExpanded={expandedSections.has('recipient')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          {/* Recipient Mode Selector */}
          <div>
            <label className={commonStyles.smallLabel}>
              Címzett típusa
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => updateConfig('recipientMode', 'single')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  recipientMode === 'single'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-300'
                }`}
              >
                <div className="text-2xl mb-1">📧</div>
                <div className="font-medium text-sm">Egy címzett</div>
                <div className="text-xs opacity-75 mt-1">Egyedi email cím</div>
              </button>
              
              <button
                type="button"
                onClick={() => updateConfig('recipientMode', 'group')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  recipientMode === 'group'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-300'
                }`}
              >
                <div className="text-2xl mb-1">👥</div>
                <div className="font-medium text-sm">Ügyfélcsoport</div>
                <div className="text-xs opacity-75 mt-1">Tömeges küldés</div>
              </button>
            </div>
          </div>

          {/* Ügyfélcsoport választó */}
          {recipientMode === 'group' && (
            <div>
              <label className={commonStyles.smallLabel}>
                Válassz ügyfélcsoportot
              </label>
              {groupsLoading ? (
                <div className="text-sm text-gray-500">Csoportok betöltése...</div>
              ) : groups.length === 0 ? (
                <div className="text-sm bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                  <p className="text-yellow-800 dark:text-yellow-200 font-medium mb-2">⚠️ Még nincs ügyfélcsoport</p>
                  <p className="text-yellow-700 dark:text-yellow-300 text-xs">
                    Hozz létre ügyfélcsoportokat az <strong>Ügyfelek</strong> menüben!
                  </p>
                </div>
              ) : (
                <select
                  value={node.data.config?.customerGroupId || ''}
                  onChange={(e) => updateConfig('customerGroupId', e.target.value)}
                  className={commonStyles.select}
                >
                  <option value="">Válassz csoportot...</option>
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.icon || '👥'} {group.name} ({group.customerCount} ügyfél)
                    </option>
                  ))}
                </select>
              )}
              
              {node.data.config?.customerGroupId && (
                <div className="mt-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    💡 <strong>Tömeges küldés:</strong> Az email minden ügyfélnek elküldésre kerül a csoportban, akinek van email címe.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Egyedi email cím - csak single mode-ban */}
          {recipientMode === 'single' && (
            <div>
              <label className={commonStyles.smallLabel}>
                Címzett
              </label>
              <input
                type="text"
                value={node.data.config.recipient || ''}
                onChange={(e) => updateConfig('recipient', e.target.value)}
                className={commonStyles.input}
                placeholder="{{customer.email}} vagy email@example.com"
              />
            </div>
          )}
        </div>
      </CollapsibleSection>
      {/* SMTP Fiók - ELSŐ, hogy azonnal lássák a credential választót */}
      <CollapsibleSection
        id="smtp"
        title="SMTP Fiók"
        icon="🔐"
        color="green"
        isExpanded={expandedSections.has('smtp')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          {/* Credential választó */}
          <div>
            <label className="flex items-center mb-3">
              <input
                type="checkbox"
                checked={useCredential}
                onChange={(e) => handleCredentialToggle(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                🔐 Mentett email fiók használata (ajánlott)
              </span>
            </label>

            {useCredential && (
              <div>
                {credentialsLoading ? (
                  <div className="text-sm text-gray-500">Betöltés...</div>
                ) : credentials.length === 0 ? (
                  <div className="text-sm bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                    <p className="text-yellow-800 dark:text-yellow-200 font-medium mb-2">⚠️ Még nincs mentett email fiók</p>
                    <p className="text-yellow-700 dark:text-yellow-300 text-xs">
                      Kattints a fejlécben a <strong>🔑 API Kulcsok</strong> gombra és hozz létre egy SMTP fiókot!
                    </p>
                  </div>
                ) : (
                  <select
                    value={node.data.config?.credentialId || ''}
                    onChange={(e) => updateConfig('credentialId', e.target.value)}
                    className={commonStyles.select}
                  >
                    <option value="">Válassz SMTP fiókot...</option>
                    {credentials.map(cred => (
                      <option key={cred.id} value={cred.id}>
                        {cred.name} ({cred.data.host})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* Manuális konfiguráció (csak ha nincs credential) */}
          {!useCredential && (
            <>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-3">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  💡 <strong>Tipp:</strong> Mentett fiók használata biztonságosabb és gyorsabb!
                </p>
              </div>

              {/* Gyors Beállítások */}
              <div>
                <label className={commonStyles.smallLabel}>
                  Gyors Beállítás
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { name: 'Gmail', host: 'smtp.gmail.com', port: 587, secure: false },
                    { name: 'Outlook', host: 'smtp-mail.outlook.com', port: 587, secure: false },
                    { name: 'Yahoo', host: 'smtp.mail.yahoo.com', port: 587, secure: false },
                    { name: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, secure: false }
                  ].map((provider) => (
                    <button
                      key={provider.name}
                      onClick={() => updateConfig('smtp', {
                        ...node.data.config.smtp,
                        host: provider.host,
                        port: provider.port,
                        secure: provider.secure
                      })}
                      className="text-xs px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      {provider.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={commonStyles.smallLabel}>
                SMTP Szerver
              </label>
              <input
                type="text"
                value={node.data.config.smtp?.host || 'smtp.gmail.com'}
                onChange={(e) => updateConfig('smtp', { ...node.data.config.smtp, host: e.target.value })}
                className={commonStyles.input}
                placeholder="smtp.gmail.com"
              />
            </div>
            
            <div>
              <label className={commonStyles.smallLabel}>
                Port
              </label>
              <input
                type="number"
                value={node.data.config.smtp?.port || 587}
                onChange={(e) => updateConfig('smtp', { ...node.data.config.smtp, port: parseInt(e.target.value) || 587 })}
                className={commonStyles.input}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={commonStyles.smallLabel}>
                Felhasználónév
              </label>
              <input
                type="text"
                value={node.data.config.smtp?.user || ''}
                onChange={(e) => updateConfig('smtp', { ...node.data.config.smtp, user: e.target.value })}
                className={commonStyles.input}
                placeholder="email@gmail.com"
              />
            </div>
            
            <div>
              <label className={commonStyles.smallLabel}>
                Jelszó / App Password
              </label>
              <input
                type="password"
                value={node.data.config.smtp?.pass || ''}
                onChange={(e) => updateConfig('smtp', { ...node.data.config.smtp, pass: e.target.value })}
                className={commonStyles.input}
                placeholder="App Password"
              />
            </div>
          </div>
          
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="smtp-secure"
                  checked={node.data.config.smtp?.secure || false}
                  onChange={(e) => updateConfig('smtp', { ...node.data.config.smtp, secure: e.target.checked })}
                  className="h-3 w-3 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="smtp-secure" className="ml-2 text-xs text-gray-700 dark:text-gray-300">
                  SSL/TLS titkosítás (465-ös port esetén)
                </label>
              </div>
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* Email Tartalom */}
      <CollapsibleSection
        id="email"
        title="Email Tartalom"
        icon="📧"
        color="blue"
        isExpanded={expandedSections.has('email')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          {/* Template választó - ugyanúgy mint az SMTP fiók */}
          <div>
            <label className="flex items-center mb-3">
              <input
                type="checkbox"
                checked={useTemplate}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUseTemplate(checked);
                  if (!checked) {
                    // Ha kikapcsoljuk, töröljük az emailTemplateId-t
                    updateConfig('emailTemplateId', undefined);
                  }
                }}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                📄 Email sablon használata (ajánlott)
              </span>
            </label>

            {useTemplate && (
              <div>
                {templatesLoading ? (
                  <div className="text-sm text-gray-500">Sablonok betöltése...</div>
                ) : templates.length === 0 ? (
                  <div className="text-sm bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                    <p className="text-yellow-800 dark:text-yellow-200 font-medium mb-2">⚠️ Még nincs email sablon</p>
                    <p className="text-yellow-700 dark:text-yellow-300 text-xs">
                      Hozz létre email sablonokat a <strong>Beállítások → Email Templates</strong> menüben!
                    </p>
                  </div>
                ) : (
                  <select
                    value={node.data.config?.emailTemplateId || ''}
                    onChange={(e) => {
                      const templateId = e.target.value;
                      console.log('📧 Email template selected:', templateId);
                      console.log('📧 Is empty?', templateId === '');
                      console.log('📧 Template ID length:', templateId.length);
                      
                      if (templateId) {
                        updateConfig('emailTemplateId', templateId);
                      } else {
                        updateConfig('emailTemplateId', undefined);
                      }
                      
                      // Log után egy kis késleltetéssel, hogy lássuk a frissített értéket
                      setTimeout(() => {
                        console.log('💾 Node config after update:', node.data.config);
                      }, 100);
                    }}
                    className={commonStyles.select}
                  >
                    <option value="">Válassz sablont...</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* Subject és Body mezők - csak ha NINCS template kiválasztva */}
          {(!useTemplate || !node.data.config?.emailTemplateId) && (
            <>
              <div>
                <label className={commonStyles.smallLabel}>
                  Tárgy
                </label>
                <input
                  type="text"
                  value={node.data.config.subject || ''}
                  onChange={(e) => updateConfig('subject', e.target.value)}
                  className={commonStyles.input}
                  placeholder="Email tárgy"
                />
              </div>

              <div>
                <label className={commonStyles.smallLabel}>
                  Üzenet
                </label>
                <textarea
                  value={node.data.config.body || ''}
                  onChange={(e) => updateConfig('body', e.target.value)}
                  rows={6}
                  className={commonStyles.textarea}
                  placeholder="Email tartalom..."
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  HTML formázás támogatott. Változók: {`{{név}}`}
                </p>
              </div>
            </>
          )}

          {/* Template preview - csak ha VAN template kiválasztva */}
          {useTemplate && node.data.config?.emailTemplateId && (() => {
            const selectedTemplate = templates.find(t => t.id === node.data.config.emailTemplateId);
            if (selectedTemplate) {
              return (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                  <p className="text-xs text-blue-700 dark:text-blue-300 font-medium mb-2">
                    📄 Email sablon: <strong>{selectedTemplate.name}</strong>
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    A tartalom futásidőben töltődik be a sablonból. A változók automatikusan feloldódnak.
                  </p>
                </div>
              );
            }
            return null;
          })()}
        </div>
      </CollapsibleSection>

      {/* Feladó Beállítások - Összecsukható */}
      <CollapsibleSection
        id="sender"
        title="Feladó Adatok"
        icon="👤"
        color="purple"
        isExpanded={expandedSections.has('sender')}
        onToggle={toggleSection}
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={commonStyles.smallLabel}>
              Feladó Név
            </label>
            <input
              type="text"
              value={node.data.config.from?.name || 'YouCRM'}
              onChange={(e) => updateConfig('from', { ...node.data.config.from, name: e.target.value })}
              className={commonStyles.input}
              placeholder="YouCRM"
            />
          </div>
          
          <div>
            <label className={commonStyles.smallLabel}>
              Feladó Email
            </label>
            <input
              type="email"
              value={node.data.config.from?.email || ''}
              onChange={(e) => updateConfig('from', { ...node.data.config.from, email: e.target.value })}
              className={commonStyles.input}
              placeholder="noreply@yourdomain.com"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Ha nincs megadva feladó email, az SMTP felhasználónevet használja
        </p>
      </CollapsibleSection>
    </div>
  );
};

export default SMTPEmailConfig;
